/**
 * Structural shapes of the document records (W1-03 core-model-ops).
 *
 * A tiny validator combinator library plus one shape per record table and singleton (contract
 * src/core/model/types.ts). Used by `deserializeDoc` (corrupt snapshots fail with a precise path such as
 * `keys[12].ease.left: expected a finite number`) and by the invariant checker (records written by commands keep
 * their types: integer fields integral, numbers finite, enums valid, beats normalised).
 *
 * Shapes are strict: unknown fields are reported (a v1 snapshot never carries fields the v1 model does not know).
 * A field whose value is `undefined` counts as absent.
 */

import { Beats } from '../time/beat';
import { isJson, isPlainObject } from '../util/json';
import { formatModelMessage, type MessageParams } from './messageFormat';
import type { SingletonName, TableName } from './types';

/**
 * What is wrong with a value: the `problem` selector of the catalogue message `model.shape.problem`
 * (core/model/model.messages.ts).
 */
export type ShapeProblemCode =
  | 'number'
  | 'numberRange'
  | 'integer'
  | 'integerRange'
  | 'string'
  | 'boolean'
  | 'oneOf'
  | 'array'
  | 'tuple'
  | 'object'
  | 'missing'
  | 'unknownField'
  | 'json'
  | 'beat'
  | 'triple'
  | 'assetId'
  | 'trackValue'
  | 'varConst';

/** Every ShapeProblemCode (tests check that the catalogue has a branch for each). */
export const SHAPE_PROBLEM_CODES: readonly ShapeProblemCode[] = [
  'number',
  'numberRange',
  'integer',
  'integerRange',
  'string',
  'boolean',
  'oneOf',
  'array',
  'tuple',
  'object',
  'missing',
  'unknownField',
  'json',
  'beat',
  'triple',
  'assetId',
  'trackValue',
  'varConst',
];

export interface ShapeProblem {
  /** e.g. `keys[12].ease.left` (relative to the value given to `checkShape`, prefixed by the caller's root). */
  readonly path: string;
  readonly code: ShapeProblemCode;
  /**
   * Parameters of `model.shape.problem` (language-neutral): `problem` (= code), `got` (preview of the value found),
   * and `min` / `max` / `count` / `values` where the code uses them.
   */
  readonly params: MessageParams;
  /** English text, e.g. `expected a finite number, got "1"`. */
  readonly problem: string;
}

/** Collector: stops recording (and descending) once `max` problems were found. */
export interface ShapeCtx {
  readonly problems: ShapeProblem[];
  readonly max: number;
}

/**
 * A shape check of value `v` found at `path` (+ `key`, when given: a field name or an array index under `path`).
 * The full path is only spelled out when a problem is reported or a value has children (W3-03, UX-029: the checker
 * walks every record of a document, so leaves never build path strings).
 */
export type Shape = (v: unknown, path: string, ctx: ShapeCtx, key?: string | number) => void;

/** The path of a value: `path`, `path.key` or `path[i]`. */
function at(path: string, key: string | number | undefined): string {
  if (key === undefined) return path;
  return typeof key === 'number' ? `${path}[${String(key)}]` : `${path}.${key}`;
}

type Extra = Readonly<Record<string, string | number>>;

function report(ctx: ShapeCtx, path: string, code: ShapeProblemCode, extra: Extra = {}): void {
  if (ctx.problems.length >= ctx.max) return;
  const params: MessageParams = { ...extra, problem: code };
  ctx.problems.push({ path, code, params, problem: formatModelMessage('shape.problem', params) });
}

/** Reports `code` with a preview of the value found. */
function reportGot(ctx: ShapeCtx, path: string, code: ShapeProblemCode, v: unknown, extra: Extra = {}): void {
  report(ctx, path, code, { ...extra, got: previewValue(v) });
}

function full(ctx: ShapeCtx): boolean {
  return ctx.problems.length >= ctx.max;
}

const PREVIEW_MAX = 40;
const PREVIEW_STRING_MAX = 24;

/**
 * Short, language-neutral preview of a value for problem texts: numbers and literals as written (`2.5`, `NaN`,
 * `null`, `true`, `undefined`), strings quoted and cut after 24 characters, arrays and objects in JSON-like form cut
 * after about 40 characters (`[1, "a", …]`, `{"a": 1}`). Never walks more than the budget, so huge values are cheap.
 */
export function previewValue(v: unknown): string {
  const out = { s: '' };
  preview(v, out, 0);
  return out.s.length > PREVIEW_MAX ? `${out.s.slice(0, PREVIEW_MAX - 1)}…` : out.s;
}

function preview(v: unknown, out: { s: string }, depth: number): void {
  if (out.s.length > PREVIEW_MAX) return;
  if (v === null || v === undefined || typeof v === 'boolean') {
    out.s += String(v);
  } else if (typeof v === 'number') {
    out.s += Object.is(v, -0) ? '-0' : String(v);
  } else if (typeof v === 'bigint') {
    out.s += `${v.toString()}n`;
  } else if (typeof v === 'string') {
    out.s += JSON.stringify(v.length > PREVIEW_STRING_MAX ? `${v.slice(0, PREVIEW_STRING_MAX)}…` : v);
  } else if (Array.isArray(v)) {
    if (depth >= 3) {
      out.s += '[…]';
      return;
    }
    out.s += '[';
    const items = v as readonly unknown[];
    for (let i = 0; i < items.length; i++) {
      if (out.s.length > PREVIEW_MAX) {
        out.s += '…';
        break;
      }
      if (i > 0) out.s += ', ';
      preview(items[i], out, depth + 1);
    }
    out.s += ']';
  } else if (isPlainObject(v)) {
    if (depth >= 3) {
      out.s += '{…}';
      return;
    }
    out.s += '{';
    let first = true;
    for (const key of Object.keys(v)) {
      if (out.s.length > PREVIEW_MAX) {
        out.s += '…';
        break;
      }
      if (!first) out.s += ', ';
      first = false;
      out.s += `${JSON.stringify(key)}: `;
      preview(v[key], out, depth + 1);
    }
    out.s += '}';
  } else if (typeof v === 'object') {
    out.s += Object.prototype.toString.call(v);
  } else {
    out.s += typeof v;
  }
}

/** Runs a shape and returns every problem (up to `max`). */
export function checkShape(shape: Shape, v: unknown, path: string, max = Number.POSITIVE_INFINITY): ShapeProblem[] {
  const ctx: ShapeCtx = { problems: [], max };
  shape(v, path, ctx);
  return ctx.problems;
}

// ---------------------------------------------------------------------------------------------------------------
// Combinators
// ---------------------------------------------------------------------------------------------------------------

export const num: Shape = (v, path, ctx, key) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) reportGot(ctx, at(path, key), 'number', v);
};

export function numRange(min: number, max: number): Shape {
  return (v, path, ctx, key) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) reportGot(ctx, at(path, key), 'number', v);
    else if (v < min || v > max) reportGot(ctx, at(path, key), 'numberRange', v, { min, max });
  };
}

export function intRange(min: number, max: number): Shape {
  return (v, path, ctx, key) => {
    if (typeof v !== 'number' || !Number.isSafeInteger(v)) reportGot(ctx, at(path, key), 'integer', v);
    else if (v < min || v > max) reportGot(ctx, at(path, key), 'integerRange', v, { min, max });
  };
}

export const int: Shape = intRange(Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
export const nonNegInt: Shape = intRange(0, Number.MAX_SAFE_INTEGER);
export const u8: Shape = intRange(0, 255);
export const i32: Shape = intRange(-2147483648, 2147483647);

export const str: Shape = (v, path, ctx, key) => {
  if (typeof v !== 'string') reportGot(ctx, at(path, key), 'string', v);
};

export const bool: Shape = (v, path, ctx, key) => {
  if (typeof v !== 'boolean') reportGot(ctx, at(path, key), 'boolean', v);
};

export function lit(...values: readonly (string | number | boolean)[]): Shape {
  const set = new Set<unknown>(values);
  const list = values.map((x) => JSON.stringify(x)).join(', ');
  return (v, path, ctx, key) => {
    if (!set.has(v)) reportGot(ctx, at(path, key), 'oneOf', v, { values: list });
  };
}

export function nullable(shape: Shape): Shape {
  return (v, path, ctx, key) => {
    if (v !== null) shape(v, path, ctx, key);
  };
}

export function arr(item: Shape): Shape {
  return (v, path, ctx, key) => {
    if (!Array.isArray(v)) {
      reportGot(ctx, at(path, key), 'array', v);
      return;
    }
    const items = v as readonly unknown[];
    if (items.length === 0) return;
    const here = at(path, key);
    for (let i = 0; i < items.length; i++) {
      if (full(ctx)) return;
      item(items[i], here, ctx, i);
    }
  };
}

export function tuple(...items: readonly Shape[]): Shape {
  const n = items.length;
  return (v, path, ctx, key) => {
    if (!Array.isArray(v) || v.length !== n) {
      reportGot(ctx, at(path, key), 'tuple', v, { count: n });
      return;
    }
    const values = v as readonly unknown[];
    const here = at(path, key);
    for (let i = 0; i < n; i++) {
      if (full(ctx)) return;
      (items[i] as Shape)(values[i], here, ctx, i);
    }
  };
}

/** Plain object with required and optional fields; any other field is a problem. */
export function obj(required: Readonly<Record<string, Shape>>, optional: Readonly<Record<string, Shape>> = {}): Shape {
  const req = Object.entries(required);
  const known = new Set([...Object.keys(required), ...Object.keys(optional)]);
  return (v, path, ctx, key) => {
    if (!isPlainObject(v)) {
      reportGot(ctx, at(path, key), 'object', v);
      return;
    }
    const here = at(path, key);
    for (const [field, shape] of req) {
      if (full(ctx)) return;
      const value = v[field];
      if (value === undefined) report(ctx, at(here, field), 'missing');
      else shape(value, here, ctx, field);
    }
    // Every own enumerable field (plain objects only: nothing is inherited) is either known or a problem.
    for (const field in v) {
      if (full(ctx)) return;
      const value = v[field];
      if (value === undefined) continue;
      if (!known.has(field)) {
        report(ctx, at(here, field), 'unknownField');
        continue;
      }
      const shape = optional[field];
      if (shape !== undefined) shape(value, here, ctx, field);
    }
  };
}

/** Discriminated union on a string tag field. */
export function tagged(tag: string, variants: Readonly<Record<string, Shape>>): Shape {
  const names = Object.keys(variants)
    .map((n) => JSON.stringify(n))
    .join(', ');
  return (v, path, ctx, key) => {
    if (!isPlainObject(v)) {
      reportGot(ctx, at(path, key), 'object', v);
      return;
    }
    const t = v[tag];
    const shape = typeof t === 'string' && Object.prototype.hasOwnProperty.call(variants, t) ? variants[t] : undefined;
    if (shape === undefined) {
      reportGot(ctx, at(at(path, key), tag), 'oneOf', t, { values: names });
      return;
    }
    shape(v, path, ctx, key);
  };
}

/** First alternative that reports nothing wins; otherwise `code` (which names the alternatives) is reported. */
export function oneOf(code: ShapeProblemCode, ...alternatives: readonly Shape[]): Shape {
  // One probe collector per combinator, reused (a nested use of the same combinator gets a fresh one).
  let spare: ShapeCtx | null = { problems: [], max: 1 };
  return (v, path, ctx, key) => {
    const probe = spare ?? { problems: [], max: 1 };
    spare = null;
    try {
      for (const shape of alternatives) {
        probe.problems.length = 0;
        shape(v, path, probe, key);
        if (probe.problems.length === 0) return;
      }
    } finally {
      probe.problems.length = 0;
      spare = probe;
    }
    reportGot(ctx, at(path, key), code, v);
  };
}

/** `shape`, then `ok(v)`; reports `code` when the shape passes but `ok` does not. */
export function refine(shape: Shape, ok: (v: unknown) => boolean, code: ShapeProblemCode): Shape {
  return (v, path, ctx, key) => {
    const before = ctx.problems.length;
    shape(v, path, ctx, key);
    if (ctx.problems.length === before && !ok(v)) reportGot(ctx, at(path, key), code, v);
  };
}

export const json: Shape = (v, path, ctx, key) => {
  if (!isJson(v)) reportGot(ctx, at(path, key), 'json', v);
};

/** Ordered unknown-key list: `[key, value]` pairs. */
export const passthrough: Shape = arr(tuple(str, json));

export const beat: Shape = refine(
  obj({ w: int, n: int, d: int }),
  (v) => Beats.isNormal(v as { w: number; n: number; d: number }),
  'beat',
);

export const triple: Shape = refine(
  tuple(int, int, int),
  (v) => Beats.tryFromTriple(v as readonly [number, number, number]) !== null,
  'triple',
);

/** Numeric record id (from the document counter). */
export const id: Shape = nonNegInt;
export const assetId: Shape = refine(str, (v) => (v as string).length > 0, 'assetId');
export const rgb: Shape = tuple(u8, u8, u8);
export const rgba8: Shape = tuple(u8, u8, u8, u8);
export const vec2: Shape = tuple(num, num);
const bezierPoints: Shape = tuple(num, num, num, num);

// ---------------------------------------------------------------------------------------------------------------
// Records
// ---------------------------------------------------------------------------------------------------------------

export const LINE_PROP_NAMES = [
  'moveX',
  'moveY',
  'rotate',
  'alpha',
  'speed',
  'scaleX',
  'scaleY',
  'color',
  'text',
  'incline',
  'paint',
  'gif',
] as const;

export const BUILTIN_SHADER_NAMES = [
  'chromatic',
  'circleBlur',
  'fisheye',
  'glitch',
  'grayscale',
  'noise',
  'pixel',
  'radialBlur',
  'shockwave',
  'vignette',
] as const;

export const UI_ELEMENTS = ['pause', 'combonumber', 'combo', 'score', 'bar', 'name', 'level'] as const;

export const easeShape: Shape = tagged('kind', {
  hold: obj({ kind: lit('hold') }, { gap: lit(true) }),
  tween: obj({ kind: lit('tween'), id: int, left: num, right: num }),
  bezier: obj({ kind: lit('bezier'), p: bezierPoints }),
});

/** Any track value: number, string, or a numeric tuple (RGB, Vec2, RGBA8); the value type is checked semantically. */
export const trackValueShape: Shape = oneOf(
  'trackValue',
  num,
  str,
  vec2,
  tuple(num, num, num),
  tuple(num, num, num, num),
);

const keySrcShape: Shape = obj(
  {},
  {
    easingType: int,
    ignoredEase: obj({ easingType: int, easingLeft: num, easingRight: num, bezier: int, bezierPoints }),
    sliceRaw: tuple(num, num),
    bezierPointsRaw: bezierPoints,
    rawEndValue: num,
    rawStart: triple,
    rawEnd: triple,
    extra: passthrough,
  },
);

export const keyShape: Shape = obj(
  { id, track: id, t: beat, vIn: trackValueShape, vOut: trackValueShape, ease: easeShape, link: nonNegInt },
  { src: keySrcShape },
);

export const trackOwnerShape: Shape = tagged('kind', {
  line: obj({ kind: lit('line'), line: id, prop: lit(...LINE_PROP_NAMES), layer: nonNegInt }),
  effectVar: obj({ kind: lit('effectVar'), effect: id, name: str }),
  videoVar: obj({ kind: lit('videoVar'), video: id, which: lit('alpha', 'dim') }),
});

export const trackShape: Shape = obj(
  { id, owner: trackOwnerShape, valueType: lit('number', 'rgb', 'string', 'vec2', 'rgba8') },
  { importedLeadIn: lit(true) },
);

const hitsoundShape: Shape = tagged('kind', {
  builtin: obj({ kind: lit('builtin'), sound: lit('tap', 'drag', 'flick') }),
  asset: obj({ kind: lit('asset'), asset: assetId }),
});

export const noteShape: Shape = obj(
  {
    id,
    line: id,
    kind: lit('tap', 'drag', 'flick', 'hold'),
    t: beat,
    end: beat,
    x: num,
    above: bool,
    fake: bool,
    speed: num,
    yOffset: num,
    size: num,
    alpha: intRange(0, 65535),
    visibleTime: nullable(num),
    hitsound: nullable(hitsoundShape),
    tint: nullable(rgb),
    tintHitEffects: nullable(rgb),
    judgeArea: nullable(num),
    gen: nullable(id),
  },
  {
    src: obj(
      {},
      { rawStart: triple, rawEnd: triple, nonHoldEnd: triple, aboveRaw: int, isFakeRaw: int, extra: passthrough },
    ),
  },
);

const controlCurveShape: Shape = obj({
  points: arr(obj({ x: num, easing: u8, value: num }, { extra: arr(tuple(str, num)) })),
});

const noteControlsShape: Shape = obj({
  pos: nullable(controlCurveShape),
  alpha: nullable(controlCurveShape),
  size: nullable(controlCurveShape),
  y: nullable(controlCurveShape),
});

export const lineShape: Shape = obj({
  id,
  name: str,
  parent: nullable(id),
  rotateWithParent: bool,
  zOrder: i32,
  cover: bool,
  texture: nullable(assetId),
  attachUI: nullable(lit(...UI_ELEMENTS)),
  gifAutoLoop: bool,
  group: nullable(id),
  controls: noteControlsShape,
  origin: lit('editor', 'import'),
  editor: obj({ tag: nullable(str), xyLinked: bool, layerNames: arr(nullable(str)) }),
  rpe: obj({
    bpmfactor: num,
    anchor: tuple(num, num),
    isCoverRaw: nullable(int),
    skewControl: nullable(json),
    layerSlots: arr(nullable(lit('null', 'object'))),
    extended: lit('object', 'null', 'absent'),
    notesKey: lit('present', 'absent'),
    ignored: arr(obj({ key: str, value: json, writeBack: lit('extended', 'sidecar') })),
    nullKeys: obj({ extended: arr(str), layers: arr(nullable(arr(str))) }),
    extra: passthrough,
    extendedExtra: passthrough,
    layerExtra: arr(nullable(passthrough)),
  }),
});

export const bpmShape: Shape = obj(
  { id, t: beat, bpm: num },
  { src: obj({}, { rawStart: triple, extra: passthrough }) },
);

export const markerShape: Shape = obj({ id, t: beat, name: str, color: nullable(str) });

export const groupShape: Shape = obj({ id, name: str, order: nonNegInt, color: nullable(str) });

export const assetShape: Shape = obj(
  {
    id: assetId,
    path: str,
    mime: str,
    bytes: nonNegInt,
    role: lit('music', 'illustration', 'texture', 'gif', 'hitsound', 'shader', 'video', 'font', 'other'),
  },
  {
    missing: bool,
    meta: obj(
      {},
      {
        width: num,
        height: num,
        durationSec: num,
        sampleRate: num,
        channels: nonNegInt,
        codec: str,
        gifFrames: nonNegInt,
        gifDelaysMs: arr(num),
        gifTotalMs: num,
        phiraDecodable: bool,
      },
    ),
  },
);

const effectVarShape: Shape = obj({
  name: str,
  type: lit('float', 'vec2', 'color'),
  value: tagged('kind', {
    null: obj({ kind: lit('null') }),
    const: obj({ kind: lit('const'), v: oneOf('varConst', num, vec2, rgba8) }),
    track: obj({ kind: lit('track'), track: id }),
  }),
});

export const effectShape: Shape = obj(
  {
    id,
    start: beat,
    end: beat,
    shader: tagged('kind', {
      builtin: obj({ kind: lit('builtin'), name: lit(...BUILTIN_SHADER_NAMES) }),
      asset: obj({ kind: lit('asset'), asset: assetId }),
    }),
    global: bool,
    vars: arr(effectVarShape),
  },
  { src: obj({}, { rawStart: triple, rawEnd: triple, extra: passthrough }) },
);

const videoVarShape: Shape = tagged('kind', {
  default: obj({ kind: lit('default') }),
  const: obj({ kind: lit('const'), v: num }),
  track: obj({ kind: lit('track'), track: id }),
});

export const videoShape: Shape = obj(
  {
    id,
    asset: assetId,
    time: beat,
    scale: lit('cropCenter', 'inside', 'fit'),
    alpha: videoVarShape,
    dim: videoVarShape,
    attachLine: nullable(id),
  },
  { src: obj({}, { extra: passthrough }) },
);

export const generatorShape: Shape = obj({
  id,
  kind: lit('fill', 'motionPath', 'clone'),
  params: json,
  outputs: obj({ notes: arr(id), keys: arr(id) }),
  live: bool,
});

// ---------------------------------------------------------------------------------------------------------------
// Singletons
// ---------------------------------------------------------------------------------------------------------------

export const metaShape: Shape = obj({
  name: str,
  composer: str,
  illustrator: str,
  charter: str,
  level: str,
  difficulty: num,
  previewStart: num,
  previewEnd: nullable(num),
  tags: arr(str),
  intro: str,
  tip: nullable(str),
  music: nullable(assetId),
  illustration: nullable(assetId),
  unlockVideo: nullable(assetId),
  rpeId: str,
  metaExtra: passthrough,
});

export const pkgShape: Shape = obj({
  aspectRatio: num,
  forceAspectRatio: bool,
  backgroundDim: num,
  lineLength: num,
  holdPartialCover: bool,
  noteUniformScale: bool,
  useAttachUiFix: bool,
  speedMode: lit('legacy', 'rpe170'),
  rpeVersion: int,
  rpeVersionRaw: nullable(obj({ kind: lit('float', 'string'), lexeme: str })),
  infoOffsetSec: num,
  exportProfile: lit('rpe-compatible', 'minimal'),
  fileNaming: lit('readable', 'rpe-id'),
  speedBakeTolPx: num,
  infoExtra: passthrough,
  server: nullable(obj({}, { id: int, uploader: int, created: str, updated: str, chartUpdated: str })),
  extraJsonRoot: nullable(passthrough),
});

export const timingShape: Shape = obj({
  offsetMs: int,
  confirmed: bool,
  meter: arr(obj({ t: beat, beatsPerBar: num })),
});

export const editorShape: Shape = obj({
  ignoredProblems: arr(str),
  previewAudioDelayMs: num,
  lastExport: nullable(obj({ at: num, seq: int, sha256: str, fileName: str })),
});

export const rpeRootShape: Shape = obj({
  multiLineString: nullable(str),
  multiScale: nullable(num),
  chartTime: nullable(num),
  xybind: nullable(bool),
  extra: passthrough,
});

export const RECORD_SHAPES: Readonly<Record<TableName, Shape>> = {
  bpm: bpmShape,
  lines: lineShape,
  tracks: trackShape,
  keys: keyShape,
  notes: noteShape,
  effects: effectShape,
  videos: videoShape,
  markers: markerShape,
  groups: groupShape,
  generators: generatorShape,
  assets: assetShape,
};

export const SINGLETON_SHAPES: Readonly<Record<SingletonName, Shape>> = {
  meta: metaShape,
  pkg: pkgShape,
  timing: timingShape,
  editor: editorShape,
  rpeRoot: rpeRootShape,
};

/** Every record table in a fixed order (snapshot order, full checks). */
export const TABLE_NAMES: readonly TableName[] = [
  'bpm',
  'lines',
  'tracks',
  'keys',
  'notes',
  'effects',
  'videos',
  'markers',
  'groups',
  'generators',
  'assets',
];

export const SINGLETON_NAMES: readonly SingletonName[] = ['meta', 'pkg', 'timing', 'editor', 'rpeRoot'];

/** Shape of a serialized snapshot (SerializedChartDoc), after migration to the current version. */
export const snapshotShape: Shape = obj({
  kind: lit('phiedit-doc'),
  version: lit(1),
  idSeq: nonNegInt,
  meta: metaShape,
  pkg: pkgShape,
  timing: timingShape,
  editor: editorShape,
  rpeRoot: rpeRootShape,
  bpm: arr(bpmShape),
  lines: arr(lineShape),
  lineOrder: arr(id),
  tracks: arr(trackShape),
  keys: arr(keyShape),
  notes: arr(noteShape),
  effects: arr(effectShape),
  effectOrder: arr(id),
  videos: arr(videoShape),
  markers: arr(markerShape),
  groups: arr(groupShape),
  generators: arr(generatorShape),
  assets: arr(assetShape),
});
